/**
 * What this revision changes that reaches beyond the curriculum itself.
 *
 * Always open, rather than a disclosure. It is the answer to the summary's
 * "Outputs affected", and a card that opens a shut disclosure had to reach
 * for it by `document.querySelector` on a `data-testid` — which made a test
 * hook into a runtime selector. The panel scrolls itself instead.
 *
 * @module components/portfolio/comparison/ProgrammeImpact
 */

import React, { useEffect, useRef } from "react";

import { derivedOutputLabel, humanizeIdentifier } from "@/components/shared/diff-labels";
import { Heading, MutedText, Panel } from "@/components/ui";
import type { ProgrammeIdentityFieldChange } from "@/server/types";

import { PrimitiveTransition } from "./change-values";

export interface ProgrammeImpactProps {
  identityChanges: readonly ProgrammeIdentityFieldChange[];
  affectedOutputs: readonly string[];
  /**
   * Changes when the reader asks to be shown this panel. A counter rather
   * than a boolean, so asking twice scrolls twice.
   */
  focusToken?: number;
  /**
   * Document level of the panel's own heading. It sits inside the overview's
   * section, so it is below that section's heading rather than beside it.
   */
  headingLevel?: 2 | 3;
}

/**
 * @example
 * ```tsx
 * <ProgrammeImpact identityChanges={changes} affectedOutputs={outputs} focusToken={token} />
 * ```
 */
export function ProgrammeImpact({
  identityChanges,
  affectedOutputs,
  focusToken = 0,
  headingLevel = 2,
}: ProgrammeImpactProps): React.JSX.Element {
  const panel = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (focusToken > 0) {
      panel.current?.scrollIntoView?.({ block: "center" });
    }
  }, [focusToken]);

  return (
    <div ref={panel} data-testid="comparison-affected-outputs">
      <Panel
        headingLevel={headingLevel}
        title="Programme impact"
        description={`${identityChanges.length} protected identity change${identityChanges.length === 1 ? "" : "s"} · ${affectedOutputs.length} affected output${affectedOutputs.length === 1 ? "" : "s"}`}
      >
        <div className="grid gap-7 lg:grid-cols-2">
          <div>
            <Heading level={headingLevel === 3 ? 4 : 3} size="md">
              Protected programme identity
            </Heading>
            {identityChanges.length > 0 ? (
              <ul className="mt-3 list-disc space-y-1 pl-5 text-md">
                {identityChanges.map((change) => (
                  <li key={change.field}>
                    <span className="font-medium">{humanizeIdentifier(change.field)}:</span>{" "}
                    <PrimitiveTransition before={change.before} after={change.after} />
                  </li>
                ))}
              </ul>
            ) : (
              <MutedText as="p" className="mt-3">
                No identity fields changed.
              </MutedText>
            )}
          </div>
          <div>
            <Heading level={headingLevel === 3 ? 4 : 3} size="md">
              Affected outputs
            </Heading>
            <MutedText as="p" className="mt-2">
              {affectedOutputs.length > 0
                ? affectedOutputs.map(derivedOutputLabel).join(", ")
                : "No derived outputs are affected."}
            </MutedText>
          </div>
        </div>
      </Panel>
    </div>
  );
}
